/*
 * *****************************************************************************
 *  Copyright (C)  Motorola Solutions, INC.
 *  All Rights Reserved.
 *  Motorola Solutions Confidential Restricted.
 *  ******************************************************************************
 */

import { createFeatureSelector, createSelector, createSelectorFactory, DefaultProjectorFn, resultMemoize } from '@ngrx/store';
import { messageFeature, MessageState } from './message.reducer';
import { selectClusterLocalApplicationPresence, selectLanguage } from '../../user/+state/user.selectors';
import { Call, Message, MessageType } from 'CalltakingCoreApi';
import { MessageErrorType } from './message.actions';
import { selectCtcStatusByName } from '../../configuration/+state/configuration.selectors';
import { SortFunctions } from '../../call/util/sort-functions';
import { TranslationLanguage, MessageInputLanguageState } from '../model/translation.model';
import { LanguageChangeSource } from '../model/language.model';
import { computeLanguageChangeTimeline, mapCodesToTranslationLanguages, resolveBannerLanguage, isSameLanguage } from '../util/translation.util';

export const selectMessageState = createFeatureSelector<MessageState>(messageFeature.name);

export const selectMessageEffectInitialized = createSelector(selectMessageState, (state) => state.initialized);

export const selectMessageStateMultipleSubscriptions = createSelector(selectMessageState, (state) => state.multipleSubscriptions);
export const selectAllMultipleMessageStateSubscriptionsConfirmed = (clusterName: string) => createSelector(selectMessageStateMultipleSubscriptions, (subscriptions) =>
    subscriptions[clusterName] ? Object.values(subscriptions[clusterName]).every(Boolean) : false);
export const selectMultipleMessageStateInitializationTime = (clusterName: string) =>
    createSelector(
        selectAllMultipleMessageStateSubscriptionsConfirmed(clusterName),
        selectClusterLocalApplicationPresence(clusterName),
        selectCtcStatusByName(clusterName),
        (subscribed, presence, ctcAvailable) => (ctcAvailable && subscribed && presence?.loggedIn && presence?.loginTime ? presence.loginTime : 0));

// Since array.filter returns a new object, any selector that filters for example a message list, will emit when the input changes, regardless of whether the filtered results change
// In order to make our selectors smarter, we provide it with a custom memorization function to compare the contents of the array, thus the selectors utilizing array.filter can emit only when the result changes.

export const arrayFilterMemoize = (projectorFn: DefaultProjectorFn<Call[]>) => resultMemoize(projectorFn, arrayIsEqual);

export function arrayIsEqual(a: any[], b: any[]): boolean {
    return a?.length === b?.length && a?.every((item) => b?.includes(item));
}

// SMS
export const selectSmsCallMap = createSelector(selectMessageState, (state) => state.smsCallMap);
export const selectCallSmsMessages = (id: string) =>
    createSelectorFactory<object, Message[]>(arrayFilterMemoize)(selectSmsCallMap, (smsCallMap: { [callId: string]: { [messageId: string]: Message } }) =>
        smsCallMap[id] ? Object.values(smsCallMap[id]).sort(SortFunctions.oldestCreatedSort) : []
    );

export const selectSmsHistoricalMap = createSelector(selectMessageState, (state) => state.historicalTextMap);
export const selectSmsHistoricalCallMessageMap = (callback: string) =>
    createSelector(selectSmsHistoricalMap,
        (historicalTextMap) => historicalTextMap[callback]);

export const selectPredefinedSms = createSelector(selectMessageState, (state) => state.predefinedSms);
export const selectLocalizedPredefinedSms = createSelector(selectPredefinedSms, selectLanguage, (messages, language) => {
    let exactMatch = messages.find((message) => message.language === language);
    return exactMatch ? exactMatch : messages.find((message) => new Intl.Locale(message.language).language === new Intl.Locale(language).language);
});

// TDD
export const selectTddCallMap = createSelector(selectMessageState, (state) => state.tddCallMap);
export const selectCallTddMessages = (id: string) =>
    createSelectorFactory<object, Message[]>(arrayFilterMemoize)(selectTddCallMap, (tddCallMap: { [callId: string]: { [messageId: string]: Message } }) =>
        tddCallMap[id] ? Object.values(tddCallMap[id]).sort(SortFunctions.oldestCreatedSort) : []
    );

export const selectPredefinedTdd = createSelector(selectMessageState, (state) => state.predefinedTdd);
export const selectLocalizedPredefinedTdd = createSelector(selectPredefinedTdd, selectLanguage, (messages, language) => {
    let exactMatch = messages.find((message) => message.language === language);
    return exactMatch ? exactMatch : messages.find((message) => new Intl.Locale(message.language).language === new Intl.Locale(language).language);
});

// DTMF
export const selectDtmfCallMap = createSelector(selectMessageState, (state) => state.dtmfCallMap);
export const selectCallDtmfMessages = (id: string) =>
    createSelectorFactory<object, Message[]>(arrayFilterMemoize)(selectDtmfCallMap, (smsCallMap: { [callId: string]: { [messageId: string]: Message } }) =>
        smsCallMap[id] ? Object.values(smsCallMap[id]).sort(SortFunctions.oldestCreatedSort) : []
    );

export const selectTddChallengeMessage = createSelector(selectLocalizedPredefinedTdd, (language) => {
    if (language) {
        const messages = Object.values(language.categories).flatMap((v) => v);
        return messages?.find((m) => m.uuid === language.queryTtyMessage);
    }
    return undefined;
});

// RTT
export const selectRttCallMap = createSelector(selectMessageState, (state) => state.rttCallMap);
export const selectCallRttMessages = (id: string) =>
    createSelectorFactory<object, Message[]>(arrayFilterMemoize)(selectRttCallMap, (smsCallMap: { [callId: string]: { [messageId: string]: Message } }) =>
        smsCallMap[id] ? Object.values(smsCallMap[id]).sort(SortFunctions.oldestCreatedSort) : []
    );

// Text Contextual
export const selectPendingMessages = createSelector(selectMessageState, (state) => state.pendingMessages);

// UUIDs of pending (local-only) messages used by message-list for spinner styling
export const selectPendingMessageIds = createSelector(selectPendingMessages, (pending) => pending.map((m) => m.uuid));

export const selectCallMessages = (id: string, type: MessageType) =>
    createSelectorFactory<object, Message[]>(arrayFilterMemoize)(
        selectCallTddMessages(id),
        selectCallSmsMessages(id),
        selectCallDtmfMessages(id),
        selectCallRttMessages(id),
        selectPendingMessages,
        (tddMessages: Message[], smsMessages: Message[], dtmfMessages: Message[], rttMessages: Message[], pendingMessages: Message[]) => {
            const pendingForCall = pendingMessages.filter((p) => p.callId === id && p.type === type);
            let serverMessages: Message[];
            switch (type) {
                case 'SMS':
                    serverMessages = smsMessages;
                    break;
                case 'DTMF':
                    serverMessages = dtmfMessages;
                    break;
                case 'TDD':
                    serverMessages = tddMessages;
                    break;
                case 'RTT':
                    serverMessages = rttMessages;
                    break;
                default:
                    serverMessages = [];
            }
            return [...serverMessages, ...pendingForCall].sort(SortFunctions.oldestCreatedSort);
        }
    );

export const selectTddMessageCount = (id: string) => createSelector(selectCallTddMessages(id), (messages) => messages.length);
export const selectRttMessageCount = (id: string) => createSelector(selectCallRttMessages(id), (messages) => messages.length);
export const selectSmsMessageCount = (id: string) => createSelector(selectCallSmsMessages(id), (messages) => messages.length);
export const selectDtmfMessageCount = (id: string) => createSelector(selectCallDtmfMessages(id), (messages) => messages.length);

export const selectLocalizedPredefinedMessages = (type: MessageType) =>
    createSelector(selectLocalizedPredefinedTdd, selectLocalizedPredefinedSms, (predefinedTddMessages, predefinedSmsMessages) => {
        switch (type) {
            case 'TDD':
                return predefinedTddMessages;
            case 'SMS':
            case 'RTT':
                return predefinedSmsMessages;
            default:
                return undefined;
        }
    });

export const selectDialogState = (callId: string) =>
    createSelector(
        selectMessageState, (messageState) => messageState.dialogState?.entities[callId] ?? null
    );

export const selectLanguageChangeTimeline = (callId: string) =>
    createSelector(
        selectCallSmsMessages(callId),
        selectCallTddMessages(callId),
        (smsMessages: Message[], tddMessages: Message[]) => computeLanguageChangeTimeline([...smsMessages, ...tddMessages])
    );

export const selectSuggestedLanguages = (callId: string) =>
    createSelector(
        selectLanguageChangeTimeline(callId),
        selectTranslationLanguages,
        (timeline, available: TranslationLanguage[]) => {
            const availableLanguages = available ?? [];
            const codes = timeline.map((entry) => entry.languageCode);
            const sortedUniqueCodes = [...new Set(codes)].sort((a, b) => a.localeCompare(b));
            return mapCodesToTranslationLanguages(sortedUniqueCodes, availableLanguages);
        }
    );

export const selectRestoredLanguageChangeSource = (callId: string) =>
    createSelector(
        selectLanguageChangeTimeline(callId),
        selectDialogState(callId),
        (timeline, dialogState) => {
            const restoredLanguage = dialogState?.capabilities?.translation?.targetLanguage ?? null;
            if (!restoredLanguage) {
                return null;
            }
            const matching = [...timeline].reverse().find((entry) => isSameLanguage(entry.languageCode, restoredLanguage));
            return matching ? matching.changeSource : LanguageChangeSource.Manual;
        }
    );

export const selectManualLanguageOverride = (callId: string) =>
    createSelector(selectMessageState, (state) => state.manualLanguageOverride[callId] ?? null);

export const selectTranslationSettings = createSelector(selectMessageState, (state) => state.translationSettings);
export const selectTranslationLanguages = createSelector(selectMessageState, (state) => state.translationLanguages);

export const selectMessageErrorsByType = createSelector(selectMessageState, (state) => state.errorsByType);
export const selectTranslationErrorIds = createSelector(selectMessageErrorsByType, (errors) => errors[MessageErrorType.TRANSLATION].ids);

export const selectHasActiveTranslationErrorForCall = (callId: string) =>
    createSelector(selectMessageErrorsByType, (errors) => {
        const bucket = errors[MessageErrorType.TRANSLATION];
        return bucket.ids.some((id) => bucket.params[id]?.callId === callId);
    });

export const selectMessageInputLanguageState = (callId: string) =>
    createSelector(
        selectSuggestedLanguages(callId),
        selectDialogState(callId),
        selectLanguageChangeTimeline(callId),
        selectRestoredLanguageChangeSource(callId),
        selectManualLanguageOverride(callId),
        selectHasActiveTranslationErrorForCall(callId),
        (suggestedLanguages, dialogState, timeline, restoredLanguageChangeSource, manualLanguageOverride, hasActiveTranslationError): MessageInputLanguageState => {
            const restoredLanguage = dialogState?.capabilities?.translation?.targetLanguage ?? null;
            const latestTimelineEntry = timeline.length > 0 ? timeline[timeline.length - 1] : null;
            const previousTimelineEntry = timeline.length > 1 ? timeline[timeline.length - 2] : null;
            return {
                suggestedLanguages,
                restoredLanguage,
                restoredLanguageChangeSource,
                latestTimelineEntry,
                resolvedBannerLanguage: resolveBannerLanguage({
                    manualLanguageOverride,
                    latestEntry: latestTimelineEntry,
                    previousEntry: previousTimelineEntry,
                    hasActiveTranslationError,
                    restoredLanguage,
                    restoredLanguageChangeSource,
                    fallbackLanguage: null
                })
            };
        }
    );

export const selectBannerLanguage = (callId: string) =>
    createSelector(selectMessageInputLanguageState(callId), (inputState) => inputState.resolvedBannerLanguage);

export const selectTranslationErrorParams = createSelector(selectMessageErrorsByType, (errors) => errors[MessageErrorType.TRANSLATION].params);
export const selectSendErrorIds = createSelector(selectMessageErrorsByType, (errors) => errors[MessageErrorType.SEND].ids);
export const selectSendErrorParams = createSelector(selectMessageErrorsByType, (errors) => errors[MessageErrorType.SEND].params);
export const selectDeliveryErrorIds = createSelector(selectMessageErrorsByType, (errors) => errors[MessageErrorType.DELIVERY].ids);
export const selectDeliveryErrorParams = createSelector(selectMessageErrorsByType, (errors) => errors[MessageErrorType.DELIVERY].params);
